import { FlashcardsService } from "@/client"
import { AppEmptyState } from "@/components/Common/AppEmptyState"
import { emptyStateCopy } from "@/components/Common/emptyStateCopy"
import { SessionCompleteSummary } from "@/components/Flashcards/SessionCompleteSummary"
import { StudySession } from "@/components/Flashcards/StudySession"
import type { StudySessionCard } from "@/components/Flashcards/StudySession"
import { PageShell, PageSkeleton } from "@/components/Layout"
import useAuth from "@/hooks/useAuth"
import {
  clearStudySession,
  loadStudySession,
  saveStudySession,
} from "@/hooks/useStudySessionStore"
import { useRouteBreadcrumbs } from "@/navigation/route-meta"
import { Button } from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useEffect, useState } from "react"

type StudyDueSearch = { subjectId?: string }

export const Route = createFileRoute("/_layout/study/due")({
  validateSearch: (search: Record<string, unknown>): StudyDueSearch => ({
    subjectId:
      typeof search.subjectId === "string" ? search.subjectId : undefined,
  }),
  component: StudyDuePage,
})

function StudyDuePage() {
  const navigate = useNavigate()
  const breadcrumbs = useRouteBreadcrumbs("study.index", [
    { label: "Due Cards" },
  ])
  const { subjectId } = Route.useSearch()
  const { user } = useAuth()
  const sessionSize = user?.study_session_size ?? 50

  const mode = "due" as const
  const paramsKey = JSON.stringify({ subjectId })

  const { data: dueData, isLoading } = useQuery({
    queryKey: ["flashcards-due", subjectId ?? "all", sessionSize],
    queryFn: () =>
      FlashcardsService.getDueCards({ subjectId, limit: sessionSize }),
  })

  const [done, setDone] = useState(false)
  const [xpEarned, setXpEarned] = useState(0)
  const [sessionCards, setSessionCards] = useState<StudySessionCard[] | null>(
    null,
  )
  const [resumeIndex, setResumeIndex] = useState(0)
  const [resumeXp, setResumeXp] = useState(0)

  useEffect(() => {
    if (!isLoading && sessionCards === null) {
      const currentParams = { subjectId }
      const restored = loadStudySession()
      const matchesSnapshot =
        restored !== null &&
        restored.mode === mode &&
        JSON.stringify(restored.params) === paramsKey &&
        restored.cards.length > 0 &&
        restored.index < restored.cards.length

      if (matchesSnapshot && restored !== null) {
        setSessionCards(restored.cards)
        setResumeIndex(restored.index)
        setResumeXp(restored.xpEarned)
      } else {
        const initialCards = dueData?.data ?? []
        setSessionCards(initialCards)
        saveStudySession({
          mode,
          params: currentParams,
          cards: initialCards,
          index: 0,
          xpEarned: 0,
          streak: 0,
          savedAt: Date.now(),
        })
      }
    }
  }, [dueData?.data, isLoading, sessionCards, paramsKey, mode, subjectId])

  const handleProgress = (index: number, xp: number) => {
    saveStudySession({
      mode,
      params: { subjectId },
      cards: sessionCards ?? [],
      index,
      xpEarned: xp,
      streak: 0,
      savedAt: Date.now(),
    })
  }

  const handleContinue = () => {
    clearStudySession()
    setDone(false)
    setSessionCards(null)
    setResumeIndex(0)
    setResumeXp(0)
    setXpEarned(0)
  }

  if (isLoading) {
    return (
      <PageShell title="Due Cards" breadcrumbs={breadcrumbs}>
        <PageSkeleton variant="detail" />
      </PageShell>
    )
  }

  const cards = sessionCards ?? dueData?.data ?? []

  if (done) {
    // StudySession invalidates the flashcards-due query on completion, so
    // dueData already reflects the next due batch — a non-empty result here
    // means there's more to study beyond this session's cap.
    const hasMoreDue = (dueData?.data.length ?? 0) > 0

    return (
      <PageShell title="Due Cards" breadcrumbs={breadcrumbs}>
        <SessionCompleteSummary
          heading="Due sprint complete!"
          cardsCompleted={cards.length}
          xpEarned={xpEarned}
          onBack={() => navigate({ to: "/study" })}
          secondaryAction={
            hasMoreDue
              ? { label: "Continue studying", onClick: handleContinue }
              : undefined
          }
        />
      </PageShell>
    )
  }

  if (cards.length === 0) {
    return (
      <PageShell title="Due Cards" breadcrumbs={breadcrumbs}>
        <AppEmptyState
          title={emptyStateCopy.flashcards.nothingDue.title}
          description={emptyStateCopy.flashcards.nothingDue.description}
          action={
            <Button onClick={() => navigate({ to: "/study" })}>
              Back to Study Hub
            </Button>
          }
        />
      </PageShell>
    )
  }

  return (
    <PageShell
      title="Due Cards"
      breadcrumbs={breadcrumbs}
      py={{ base: 5, md: 8 }}
      contentGap={5}
    >
      <StudySession
        cards={cards}
        modeLabel="Due sprint"
        initialIndex={resumeIndex}
        initialXp={resumeXp}
        onProgress={handleProgress}
        onComplete={(xp) => {
          clearStudySession()
          setDone(true)
          setXpEarned(xp)
        }}
      />
    </PageShell>
  )
}

export default StudyDuePage
